import { hydrateFromWindow } from "../util/ssr.js";
import { isReadableAtom } from "../util/nanostores.js";
import { isGetHook, isMutatorHook, isStore } from "./client.js";
import { listenKeys } from "nanostores";
import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";

//#region src/client/react.ts
function createReactHook(hook) {
	return ({ path, query } = {}) => {
		const pathParamValues = path ? Object.values(path) : [];
		const queryParamValues = query ? Object.values(query) : [];
		const store = useMemo(() => hook.store({
			path,
			query
		}), [hook, ...[...pathParamValues, ...queryParamValues]]);
		if (typeof window === "undefined") return store.get();
		return useStore(store);
	};
}
function createReactMutator(hook) {
	return () => {
		return useStore(useMemo(() => hook.mutatorStore, [hook]));
	};
}
function createReactStore(hook) {
	if (isReadableAtom(hook.obj)) return () => useStore(hook.obj);
	return () => {
		const result = {};
		for (const key in hook.obj) {
			if (!Object.prototype.hasOwnProperty.call(hook.obj, key)) continue;
			const value = hook.obj[key];
			if (isReadableAtom(value)) result[key] = useStore(value);
			else result[key] = value;
		}
		return result;
	};
}
function useFragno(clientObj) {
	const result = {};
	for (const key in clientObj) {
		if (!Object.prototype.hasOwnProperty.call(clientObj, key)) continue;
		const hook = clientObj[key];
		if (isGetHook(hook)) result[key] = createReactHook(hook);
		else if (isMutatorHook(hook)) result[key] = createReactMutator(hook);
		else if (isStore(hook)) result[key] = createReactStore(hook);
		else result[key] = hook;
	}
	return result;
}
function useStore(store, options = {}) {
	const snapshotRef = useRef(store.get());
	const { keys, deps = [store, keys] } = options;
	const subscribe = useCallback((onChange) => {
		const emitChange = (value) => {
			if (snapshotRef.current === value) return;
			snapshotRef.current = value;
			onChange();
		};
		emitChange(store.value);
		if (keys?.length) return listenKeys(store, keys, emitChange);
		return store.listen(emitChange);
	}, deps);
	const get = () => snapshotRef.current;
	return useSyncExternalStore(subscribe, get, () => {
		return get();
	});
}
function FragnoHydrator({ children }) {
	useMemo(() => {
		hydrateFromWindow();
	}, []);
	return children;
}

//#endregion
export { FragnoHydrator, useFragno, useStore };
//# sourceMappingURL=react.js.map